package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.WorkItemDto
import dev.lingvo.shared.WorkSectionDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.PlainLinkStyle
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArrowRightIcon
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCommentIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LListChecksIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSparkleIcon
import dev.lingvo.site.ds.LSquarePenIcon
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.dom.Text

/**
 * The only queue.
 *
 * The landing page for translators and viewers, and the answer to "what should I do now" for
 * everyone else. Five destinations used to answer that question; a translator should not have to
 * work out which one is authoritative.
 *
 * **Nothing about seats, invoices, credits or API keys belongs here for anyone, including owners.**
 * The endpoint does not send them and this page does not ask.
 */
@Page("/app/work")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkPage() {
    var refreshKey by remember { mutableStateOf(0) }
    // The queue answers for the workspace the app is showing. Asking for all of them at once cost
    // every workspace's scan and mixed their counts into one sentence.
    val workspace = WorkspaceSelection.current()?.slug
    // `loadReloadable`, not `loadAsync(refreshKey)`. The refresh counter was a *key*, so pressing
    // it discarded the queue before the request was sent and a translator watched their own list
    // blank to skeleton bars. The token re-runs the same request and keeps the last good queue up.
    val reloadable = loadReloadable(workspace, reloadToken = refreshKey) { ApiClient.work(workspace) }
    val state = reloadable.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        // One honest sentence, from the server once it arrives, and the standing line until then.
        // Counting client-side would mean a number assembled from whatever happened to load.
        PageHeader(
            t("app.work.your_work", "Your work"),
            (state as? Async.Success)?.value?.summary
                ?: t("app.work.what_is_waiting_on_you", "What is waiting on you."),
        ) {
            // The queue is what somebody leaves open beside the editor. Only the control that was
            // pressed reports work; the rows underneath stay readable while it runs.
            LIconAction(
                label = t("app.work.refresh_your_queue", "Refresh your queue"),
                onClick = { refreshKey++ },
                loading = reloadable.refreshing,
                enabled = !reloadable.refreshing,
            ) { LRefreshIcon() }
        }
        reloadable.refreshError?.let { failure ->
            LAlert(
                t("app.work.the_refresh_did_not_replace_this", "The refresh did not replace this queue"),
                failure.message,
                variant = LAlertVariant.WARNING,
                live = true,
                dense = true,
            )
        }

        LAsyncContent(
            state,
            rows = 4,
            loadingLabel = t("app.work.loading_your_work", "Loading your work"),
            // The queue is the reader's own, so it cannot fail as a refusal or as something that
            // is not there. What is left is a fault, which wants the message and the retry.
            failure = { failure -> ErrorState(failure.message, onRetry = { refreshKey++ }) },
        ) { work ->
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                nextAction(work.sections)?.let { (section, item) -> ContinueAction(section, item) }
                work.sections.forEach { section -> WorkSection(section) }

                if (work.sections.none { it.items.isNotEmpty() }) {
                    EmptyQueue(work.projects, work.invitedByName)
                }
            }
        }
    }
}

/**
 * The one thing to do next, taken from the order the server already decided.
 *
 * The queue answers "what is waiting on me" and then leaves the reader to pick a row. That is a
 * choice nobody wants to make twenty times a day, and it is the choice the sections are already
 * ordered to make for them: review first, then work sent back with a note, then strings whose
 * English moved, then proposals. The first item of the first non-empty section is the answer, and
 * it does not need a second opinion computed here.
 *
 * Returns null when nothing is waiting, which is [EmptyQueue]'s job and not a button's.
 */
internal fun nextAction(sections: List<WorkSectionDto>): Pair<WorkSectionDto, WorkItemDto>? =
    sections.firstNotNullOfOrNull { section -> section.items.firstOrNull()?.let { section to it } }

/**
 * One obvious next action, above a queue that otherwise opens with a list of choices.
 *
 * It repeats a row that appears again below, deliberately: the row is where you go to *choose*, and
 * this is where you go when you do not want to. Both name the same destination and the same count,
 * because a shortcut that lands somewhere other than the row it shortcuts is worse than no shortcut.
 *
 * A link, not a button. Its whole job is going somewhere, and a translator who wants the queue
 * beside what they already have should be able to open it in a tab like every other row here.
 */
@Composable
private fun ContinueAction(section: WorkSectionDto, item: WorkItemDto) {
    LCard(Modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().gap(Space.MD),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(Modifier.gap(Space.XXS)) {
                // The section's own title, so the button's context is the reason it is first —
                // "Changes requested" says something a project and locale cannot. The glyph is the
                // same one the section below carries, so the shortcut and the row it shortcuts read
                // as the same thing — but this is deliberately **not** a heading: the section below
                // already owns that title in the outline, and two `H2`s with identical text is a
                // reader moving by heading and arriving twice at the same place.
                Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                    SectionGlyph(section.id)
                    LMuted(section.title)
                }
                Text(
                    tf(
                        "app.work.next_project_and_locale",
                        "{project} · {locale} · {count}",
                        "project" to item.projectName,
                        // The language, not the code. Every other surface that names this queue's
                        // rows — the rail, the dashboard, the project card, the insights bars —
                        // says "French"; only here did `fr` reach a reader as a label.
                        "locale" to localeInfo(item.locale).englishName,
                        "count" to tPlural("app.work.count.string", item.count, "{count} string", "{count} strings"),
                    ),
                )
            }
            LLinkButton(
                t("app.work.continue", "Continue"),
                path = item.href,
                leadingIcon = { LArrowRightIcon() },
                // "Continue" on its own is a destination nobody named. The override says where, and
                // still contains the visible word, which is what WCAG 2.5.3 asks of a speech user
                // saying "click Continue".
                accessibleLabel = tf(
                    "app.work.continue_with_project_in_locale",
                    "Continue with {project} in {locale}",
                    "project" to item.projectName,
                    "locale" to localeInfo(item.locale).englishName,
                ),
            )
        }
    }
}

/**
 * A card per section, and the card's own name is a heading.
 *
 * These were `H3`s directly under the page's `H1`, with no `H2` anywhere in the file — a level
 * skipped is a level a screen reader reports as a gap in the outline. [LSectionTitle] is the same
 * decision made the same way on every card that carries one, at the size a card's identity takes.
 */
@Composable
private fun WorkSection(section: WorkSectionDto) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                LSectionTitle(
                    section.title,
                    Modifier.fontSize(TypeScale.HeadingSmall),
                    icon = { SectionGlyph(section.id) },
                )
                if (section.items.isNotEmpty()) LBadge(formatCount(section.total))
            }
            // A section that failed says so and leaves the rest of the page alone. Losing one
            // heading is recoverable; losing the queue means nobody can start.
            section.error?.let { message ->
                LAlert(t("app.work.couldnt_load_this_section", "Couldn't load this section"), message, variant = LAlertVariant.WARNING)
            }
            section.items.forEach { item -> WorkRow(item) }
        }
    }
}

/**
 * The section's mark, from the id the server sends rather than from its title.
 *
 * The title is prose and is translated; the id is the contract. Matching on the title would make
 * the glyph disappear the moment somebody reworded "Changes requested".
 */
@Composable
private fun SectionGlyph(id: String) {
    when (id) {
        "review" -> LListChecksIcon()
        "changes" -> LSquarePenIcon()
        "recheck" -> LRefreshIcon()
        "proposals" -> LSparkleIcon()
        "mentions" -> LCommentIcon()
        else -> LListChecksIcon()
    }
}

@Composable
private fun WorkRow(
    item: WorkItemDto,
    /**
     * Whether this row's count is untranslated work a machine could start on.
     *
     * True only for the launcher rows, which the server builds from what is still untranslated —
     * every other section counts work that already has a human's text in it, and offering to
     * machine-translate over that is offering to destroy it.
     */
    pretranslatable: Boolean = false,
) {
    Row(
        Modifier.fillMaxWidth().gap(Space.SM),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // The href comes from the server with its filter already applied, so what this opens is
        // exactly the set the number counts.
        //
        // Styled, because a bare `Link` inherits the user agent's own link colour — pure `blue` in
        // light and pure `aqua` in dark, neither of which is in either palette. Four of those beside
        // the lavender accent gives the page two accents, one of them a CSS keyword. The dashboard's
        // counters already answer this the same way.
        Link(
            path = item.href,
            modifier = PlainLinkStyle.toModifier()
                .color(ColorMode.current.toSitePalette().accent),
        ) {
            Text(
                tf(
                    "app.work.project_and_locale",
                    "{project} · {locale}",
                    "project" to item.projectName,
                    "locale" to localeInfo(item.locale).englishName,
                ),
            )
        }
        LMuted(tPlural("app.work.count.string", item.count, "{count} string", "{count} strings"))
        if (pretranslatable && item.count > 0) {
            // The count is the server's, and it is the count the run would price. The estimate and
            // the confirm live on `/pretranslate`, which verifies the project authority this page
            // does not hold — a workspace role is not a project role.
            LLinkButton(
                label = tPlural(
                    "app.work.count.pre_translate_string",
                    item.count,
                    "Pre-translate {count} string",
                    "Pre-translate {count} strings",
                ),
                path = "/app/projects/${item.projectSlug}/pretranslate?locale=${item.locale}",
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                leadingIcon = { LWandSparklesIcon() },
                accessibleLabel = tf(
                    "app.work.pre_translate_project_in_locale",
                    "Pre-translate {project} in {locale}",
                    "project" to item.projectName,
                    "locale" to item.locale,
                ),
            )
        }
    }
}

/**
 * Still a launcher, never a dead end.
 *
 * An empty queue is the most common state for somebody who has just been invited, and "nothing is
 * waiting on you" alone leaves them with nowhere to click.
 */
@Composable
internal fun EmptyQueue(projects: List<WorkItemDto>, invitedByName: String?) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            when {
                // A different and better sentence for somebody on their first day. Naming a person
                // is what makes it actionable.
                projects.isEmpty() && invitedByName != null -> {
                    LSectionTitle(
                        t("app.work.nothing_assigned_yet", "Nothing assigned yet"),
                        Modifier.fontSize(TypeScale.HeadingSmall),
                    )
                    LMuted(
                        tf(
                            "app.work.ask_name_which_language_to_start",
                            "Ask {name} which language to start on.",
                            "name" to invitedByName,
                        ),
                    )
                    // Naming the person is what makes the sentence actionable, but on somebody's
                    // first day "ask them" is still the only thing on the page. The projects list
                    // is the one place they can look without waiting for a reply.
                    Link(path = "/app/projects") {
                        Text(t("app.work.see_what_is_here", "See what is here"))
                    }
                }
                projects.isEmpty() -> {
                    LSectionTitle(
                        t("app.work.nothing_is_waiting_on_you", "Nothing is waiting on you"),
                        Modifier.fontSize(TypeScale.HeadingSmall),
                    )
                    LMuted(t("app.work.when_somebody_adds_strings_or_asks", "When somebody adds strings or asks for a review, it will appear here."))
                    // The component's own promise is "still a launcher, never a dead end", and this
                    // branch was a heading and a sentence. An empty *queue* does not mean an empty
                    // workspace — these are work items, not projects — so there is somewhere to go.
                    Link(path = "/app/projects") {
                        Text(t("app.work.browse_the_projects", "Browse the projects"))
                    }
                }
                else -> {
                    LSectionTitle(
                        t("app.work.nothing_is_waiting_on_you", "Nothing is waiting on you"),
                        Modifier.fontSize(TypeScale.HeadingSmall),
                    )
                    LMuted(t("app.work.here_is_what_is_still_untranslated", "Here is what is still untranslated, if you want somewhere to start."))
                    projects.forEach { project -> WorkRow(project, pretranslatable = true) }
                }
            }
        }
    }
}
